import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import DataTable from 'react-data-table-component';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import TabBar from '../../../shared/layout/tabs/TabBar';
import TabButton from '../../../shared/layout/tabs/TabButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import { MdPeople, MdPersonAdd, MdRefresh, MdEdit, MdDelete, MdBarChart, MdVerified } from 'react-icons/md';
import { approveAdminUserTermination, createAdminUser, deleteAdminUser, fetchAdminUsers, fetchAdminRoles, fetchAdminOrganizations, updateAdminUser } from '../../../services/Http/AdminApi';
import { fetchCountryCodes } from '../../../services/Http/Api';
import AdminUserStatistics from './AdminUserStatistics';
import CitySearchSelect from '../../global/CitySearchSelect';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import CountrySelect from '../../../shared/layout/forms/CountrySelect';

const AdminUsers = ({ defaultTab = 'list' }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(defaultForm());
    const [editingId, setEditingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [meta, setMeta] = useState({});
    const [totalRows, setTotalRows] = useState(0);
    const [roles, setRoles] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [countryOptions, setCountryOptions] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filters, setFilters] = useState({
        role: '',
        country_id: '',
        city: '',
        city_id: '',
        organization_id: '',
        default_password: '',
    });
    const [pendingDeleteUser, setPendingDeleteUser] = useState(null);
    const [pendingTerminationApprovalUser, setPendingTerminationApprovalUser] = useState(null);

    const { role, country_id, city, organization_id, default_password } = filters;

    useEffect(() => {
        setActiveTab(defaultTab);
    }, [defaultTab]);

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(timeout);
    }, [search]);

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchAdminUsers({
                per_page: perPage,
                page: currentPage,
                search: debouncedSearch,
                role,
                country_id,
                city,
                organization_id,
                default_password,
                sort_by: sortBy,
                sort_dir: sortDirection,
            });
            // Laravel paginator returns { data, meta } shape
            const data = Array.isArray(response) ? response : response.data || [];
            const metaData = response.meta || {};
            setUsers(data);
            setMeta(metaData);
            setTotalRows(metaData.total ?? data.length);
            if (metaData.current_page) {
                setCurrentPage(metaData.current_page);
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [city, country_id, currentPage, debouncedSearch, organization_id, default_password, perPage, role, sortBy, sortDirection]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const loadRoles = async () => {
        try {
            const data = await fetchAdminRoles();
            setRoles(Array.isArray(data) ? data : []);
        } catch (err) {
            // non-blocking
        }
    };

    const loadCountries = async () => {
        try {
            const data = await fetchCountryCodes();
            setCountryOptions(Array.isArray(data) ? data : []);
        } catch (err) {
            // non-blocking
        }
    };

    const loadOrganizations = async () => {
        try {
            const resp = await fetchAdminOrganizations({ per_page: 200 });
            setOrganizations(resp?.data || resp || []);
        } catch (err) {
            // non-blocking
        }
    };

    useEffect(() => {
        loadRoles();
        loadCountries();
        loadOrganizations();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await updateAdminUser(editingId, form);
            } else {
                await createAdminUser(form);
            }
            resetForm();
            setShowModal(false);
            await loadUsers();
        } catch (err) {
            setError(err);
        }
    };

    const handleEdit = (user) => {
        setEditingId(user.id);
        setForm({
            name: user.name || '',
            surname: user.surname || '',
            email: user.email || '',
            phone_number: user.phone_number || '',
            country_id: user.country_id || '',
            city_id: user.city_id || '',
            city: user.city || '',
            address: user.address || '',
            organization_id: user.organization_id || '',
            roles: (user.roles || []).map((r) => ({
                role_id: r.id,
                organization_id: r.pivot?.organization_id || '',
                venue_id: r.pivot?.venue_id || '',
            })),
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        try {
            await deleteAdminUser(id);
            await loadUsers();
        } catch (err) {
            setError(err);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setForm(defaultForm());
        setShowAdvanced(false);
    };

    const totalUsers = useMemo(() => meta.total || totalRows || users.length, [meta, totalRows, users.length]);
    const tableStyles = useMemo(() => getAdminTableStyles(theme), [theme]);
    const showList = activeTab === 'list';
    const columns = useMemo(() => [
        {
            name: t('AdminUsers.columns.name'),
            id: 'name',
            selector: (row) => `${row.name || ''} ${row.surname || ''}`.trim() || '-',
            sortable: true,
            sortField: 'name',
            wrap: true,
        },
        {
            name: t('AdminUsers.columns.email'),
            id: 'email',
            selector: (row) => row.email || '-',
            sortable: true,
            sortField: 'email',
        },
        {
            name: t('AdminUsers.columns.phone'),
            id: 'phone',
            selector: (row) => row.phone_number || '-',
        },
        {
            name: t('AdminUsers.columns.city'),
            id: 'city',
            selector: (row) => row.city || '-',
            sortable: true,
            sortField: 'city',
        },
        {
            name: t('AdminUsers.columns.roles'),
            id: 'roles',
            cell: (row) => (
                <RoleList>
                    {(row.roles || []).map((r) => (
                        <RoleBadge key={`${row.id}-${r.id}`}>{r.name}</RoleBadge>
                    ))}
                </RoleList>
            ),
        },
        {
            name: t('AdminUsers.columns.created'),
            id: 'created_at',
            selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'),
            sortable: true,
            sortField: 'created_at',
        },
        {
            name: t('AdminUsers.columns.defaultPassword'),
            id: 'has_default_password',
            cell: (row) => row.has_default_password ? <RoleBadge style={{ background: 'var(--color-primary-bg-soft)', color: 'var(--color-primary)' }}>{t('Common.yes')}</RoleBadge> : <RoleBadge>{t('Common.no')}</RoleBadge>,
            sortable: false,
        },
        {
            name: t('AdminUsers.columns.terminationStatus'),
            id: 'termination_status',
            selector: (row) => row.termination_status || 'active',
            cell: (row) => {
                const status = row.termination_status || 'active';
                if (status === 'pending') {
                    return <RoleBadge style={{ background: 'var(--color-warning-bg-soft)', color: 'var(--color-warning)' }}>{t('AdminUsers.termination.pending')}</RoleBadge>;
                }
                return <RoleBadge>{t('AdminUsers.termination.active')}</RoleBadge>;
            },
            sortable: false,
        },
        {
            name: t('AdminUsers.columns.actions'),
            id: 'actions',
            cell: (row) => (
                <InlineActions>
                    <IconButton type="button" onClick={() => handleEdit(row)} title={t('Common.edit')}>
                        <MdEdit size={18} />
                    </IconButton>
                    {row.termination_status === 'pending' && (
                        <IconButton
                            type="button"
                            onClick={() => setPendingTerminationApprovalUser(row)}
                            title={t('AdminUsers.actions.approveTermination')}
                        >
                            <MdVerified size={18} />
                        </IconButton>
                    )}
                    <IconButton type="button" onClick={() => setPendingDeleteUser(row)} title={t('Common.delete')}>
                        <MdDelete size={18} />
                    </IconButton>
                </InlineActions>
            ),
            ignoreRowClick: true,
        },
    ], [handleEdit, t]);

    const handleSort = (column, direction) => {
        const field = column.sortField || 'created_at';
        setSortBy(field);
        setSortDirection(direction);
        setCurrentPage(1);
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('AdminUsers.title')}</PageTitle>
            </PageHeader>
            <TabBar>
                <TabButton
                    active={activeTab === 'list'}
                    onClick={() => setActiveTab('list')}
                    icon={<MdPeople className="icon" />}
                >
                    {t('AdminUsers.tabs.users')}
                </TabButton>
                <TabButton
                    active={activeTab === 'stats'}
                    onClick={() => setActiveTab('stats')}
                    icon={<MdBarChart className="icon" />}
                >
                    {t('AdminUsers.tabs.statistics')}
                </TabButton>
            </TabBar>
            <PageContent>
                {activeTab === 'stats' && <AdminUserStatistics embedded />}
                {activeTab === 'list' && (
                    <>
                        <StatsGrid>
                            <StatCard icon={<MdPeople size={40} />} title={t('AdminUsers.stats.totalUsers')} value={totalUsers.toLocaleString()} color="users" />
                            <StatCard icon={<MdPersonAdd size={40} />} title={t('AdminUsers.stats.mode')} value={editingId ? t('AdminUsers.stats.editingUser') : t('AdminUsers.stats.createNew')} color="venues" />
                        </StatsGrid>

                        <FiltersBar>
                            <SearchInput
                                placeholder={t('AdminUsers.filters.searchPlaceholder')}
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                            />
                            <Select value={filters.role} onChange={(e) => { setFilters((f) => ({ ...f, role: e.target.value })); setCurrentPage(1); }}>
                                <option value="">{t('AdminUsers.filters.allRoles')}</option>
                                {roles.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                            </Select>
                            <Select value={filters.organization_id} onChange={(e) => { setFilters((f) => ({ ...f, organization_id: e.target.value })); setCurrentPage(1); }}>
                                <option value="">{t('AdminUsers.filters.allOrganizations')}</option>
                                {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
                            </Select>
                            <CountrySelect
                                countries={countryOptions}
                                value={filters.country_id}
                                onChange={(_, nextValue) => {
                                    setFilters((f) => ({ ...f, country_id: nextValue || '', city: '', city_id: '' }));
                                    setCurrentPage(1);
                                }}
                                inputId="admin-users-filter-country"
                                placeholder={t('AdminUsers.filters.allCountries')}
                                menuPortalTarget={document.body}
                            />
                            <Select value={filters.default_password} onChange={(e) => { setFilters((f) => ({ ...f, default_password: e.target.value })); setCurrentPage(1); }}>
                                <option value="">{t('AdminUsers.filters.anyPasswordState')}</option>
                                <option value="true">{t('AdminUsers.filters.defaultPassword')}</option>
                                <option value="false">{t('AdminUsers.filters.changedPassword')}</option>
                            </Select>
                            <CitySearchSelect
                                countryId={filters.country_id || null}
                                cityId={filters.city_id || null}
                                value={filters.city}
                                useAdminApi
                                placeholder={t('Common.city')}
                                onChange={(selectedCity) => {
                                    setFilters((f) => ({
                                        ...f,
                                        city: selectedCity?.name || '',
                                        city_id: selectedCity?.id || '',
                                    }));
                                    setCurrentPage(1);
                                }}
                            />
                            <IconButton type="button" onClick={() => loadUsers()} title={t('AdminUsers.actions.refreshList')}>
                                <MdRefresh size={18} />
                            </IconButton>
                        </FiltersBar>

                        <Card>
                            <CardHeader>
                                <h4>{t('AdminUsers.listTitle')}</h4>
                                <CardActions>
                                    <PrimaryButton type="button" onClick={() => { resetForm(); setShowModal(true); }}>
                                        <MdPersonAdd size={18} />
                                        {t('AdminUsers.actions.addUser')}
                                    </PrimaryButton>
                                </CardActions>
                            </CardHeader>
                            {error && <ErrorMessage>{t('AdminUsers.errors.load')}</ErrorMessage>}
                            <DataTable
                                columns={columns}
                                data={users}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                onChangePage={(page) => setCurrentPage(page)}
                                onChangeRowsPerPage={(newPerPage, page) => {
                                    setPerPage(newPerPage);
                                    setCurrentPage(page);
                                }}
                                highlightOnHover
                                progressPending={loading}
                                progressComponent={<LoadingMessage>{t('AdminUsers.loading')}</LoadingMessage>}
                                sortServer
                                onSort={handleSort}
                                paginationPerPage={perPage}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                persistTableHead
                                noDataComponent={t('AdminUsers.empty')}
                                customStyles={tableStyles}
                            />
                        </Card>
                    </>
                )}
            </PageContent>
            <StandardModal
                isOpen={Boolean(pendingDeleteUser)}
                onClose={() => setPendingDeleteUser(null)}
                title={t('AdminUsers.deleteModal.title')}
                footer={(
                    <>
                        <SecondaryButton type="button" onClick={() => setPendingDeleteUser(null)}>{t('Common.cancel')}</SecondaryButton>
                        <PrimaryButton
                            type="button"
                            onClick={async () => {
                                const user = pendingDeleteUser;
                                setPendingDeleteUser(null);
                                if (user) {
                                    await handleDelete(user.id);
                                }
                            }}
                        >
                            {t('Common.delete')}
                        </PrimaryButton>
                    </>
                )}
            >
                <p style={{ margin: 0 }}>
                    {t('AdminUsers.deleteModal.confirmPrefix')} <strong>{pendingDeleteUser ? `${pendingDeleteUser.name || ''} ${pendingDeleteUser.surname || ''}`.trim() : ''}</strong>?
                </p>
                <p style={{ margin: '0.5rem 0 0', fontSize: 12, color: theme.colors.textMuted }}>
                    {t('AdminUsers.deleteModal.description')}
                </p>
            </StandardModal>
            <StandardModal
                isOpen={Boolean(pendingTerminationApprovalUser)}
                onClose={() => setPendingTerminationApprovalUser(null)}
                title={t('AdminUsers.termination.modalTitle')}
                footer={(
                    <>
                        <SecondaryButton type="button" onClick={() => setPendingTerminationApprovalUser(null)}>{t('Common.cancel')}</SecondaryButton>
                        <PrimaryButton
                            type="button"
                            onClick={async () => {
                                const user = pendingTerminationApprovalUser;
                                setPendingTerminationApprovalUser(null);
                                if (user) {
                                    await approveAdminUserTermination(user.id);
                                    await loadUsers();
                                }
                            }}
                        >
                            {t('AdminUsers.actions.approveTermination')}
                        </PrimaryButton>
                    </>
                )}
            >
                <p style={{ margin: 0 }}>
                    {t('AdminUsers.termination.confirmPrefix')} <strong>{pendingTerminationApprovalUser ? `${pendingTerminationApprovalUser.name || ''} ${pendingTerminationApprovalUser.surname || ''}`.trim() : ''}</strong>?
                </p>
                <p style={{ margin: '0.5rem 0 0', fontSize: 12, color: theme.colors.textMuted }}>
                    {t('AdminUsers.termination.confirmDescription')}
                </p>
            </StandardModal>
            {showModal && (
                <ModalOverlay>
                    <ModalCard>
                        <CardHeader>
                            <h4>{editingId ? t('AdminUsers.modal.editTitle') : t('AdminUsers.modal.createTitle')}</h4>
                            <CardActions>
                                <IconButton type="button" onClick={() => { setShowModal(false); resetForm(); }} title="Close">
                                    ✕
                                </IconButton>
                            </CardActions>
                        </CardHeader>
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Input name="name" value={form.name} onChange={handleChange} placeholder={t('Common.name')} required />
                                <Input name="surname" value={form.surname} onChange={handleChange} placeholder={t('Common.surname')} required />
                            </Row>
                            <Row>
                                <Input name="email" value={form.email} onChange={handleChange} placeholder={t('Common.email')} type="email" required />
                            </Row>
                            <Accordion>
                                <AccordionHeader onClick={() => setShowAdvanced((v) => !v)}>
                                    <span>{t('AdminUsers.modal.additionalDetails')}</span>
                                    <span>{showAdvanced ? '−' : '+'}</span>
                                </AccordionHeader>
                                {showAdvanced && (
                                    <AccordionBody>
                                        <Row>
                                            <Input name="phone_number" value={form.phone_number} onChange={handleChange} placeholder={t('Common.phone')} />
                                            <Select name="phone_country_id" value={form.phone_country_id} onChange={handleChange}>
                                                <option value="">{t('AdminUsers.modal.phoneCountry')}</option>
                                                {countryOptions.map((c) => (
                                                    <option key={c.id || c.code} value={c.id || c.code}>{c.name} (+{c.code ?? c.phone_code})</option>
                                                ))}
                                            </Select>
                                        </Row>
                                        <Row>
                                            <CountrySelect
                                                countries={countryOptions}
                                                value={form.country_id}
                                                onChange={(_, nextValue) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        country_id: nextValue || '',
                                                        city: '',
                                                        city_id: '',
                                                    }));
                                                }}
                                                inputId="admin-user-country"
                                                placeholder={t('Common.country')}
                                                menuPortalTarget={document.body}
                                            />
                                            <CitySearchSelect
                                                countryId={form.country_id || null}
                                                cityId={form.city_id || null}
                                                value={form.city}
                                                useAdminApi
                                                placeholder={t('Common.city')}
                                                onChange={(selectedCity) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        city: selectedCity?.name || '',
                                                        city_id: selectedCity?.id || '',
                                                    }))
                                                }
                                            />
                                            <Input name="address" value={form.address} onChange={handleChange} placeholder={t('Common.address')} />
                                        </Row>
                                        <Row>
                                            <Select name="organization_id" value={form.organization_id} onChange={handleChange}>
                                                <option value="">{t('AdminUsers.modal.selectOrganization')}</option>
                                                {organizations.map((org) => (
                                                    <option key={org.id} value={org.id}>{org.name}</option>
                                                ))}
                                            </Select>
                                        </Row>
                                    </AccordionBody>
                                )}
                            </Accordion>
                            <FieldLabel>{t('AdminUsers.modal.roles')}</FieldLabel>
                            <RoleGrid>
                                {roles.map((role) => {
                                    const checked = (form.roles || []).some((r) => Number(r.role_id) === Number(role.id));
                                    return (
                                        <RoleChip key={role.id} $checked={checked}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        setForm((prev) => {
                                                            const existing = prev.roles || [];
                                                            if (e.target.checked) {
                                                                return { ...prev, roles: [...existing, { role_id: role.id, organization_id: prev.organization_id || '' }] };
                                                            }
                                                            return { ...prev, roles: existing.filter((r) => Number(r.role_id) !== Number(role.id)) };
                                                        });
                                                    }}
                                                />
                                                <span>{role.name}</span>
                                            </label>
                                        </RoleChip>
                                    );
                                })}
                            </RoleGrid>
                            <Actions>
                                <PrimaryButton type="submit">{editingId ? t('AdminUsers.actions.updateUser') : t('AdminUsers.actions.createUser')}</PrimaryButton>
                                <SecondaryButton type="button" onClick={() => { setShowModal(false); resetForm(); }}>{t('Common.cancel')}</SecondaryButton>
                            </Actions>
                        </Form>
                    </ModalCard>
                </ModalOverlay>
            )}
        </PageContainer>
    );
};

const defaultForm = () => ({
    name: '',
    surname: '',
    email: '',
    password: '',
    phone_number: '',
    country_id: '',
    city_id: '',
    organization_id: '',
    city: '',
    address: '',
    roles: [],
});

const Card = styled.div`
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 12px;
    padding: 1rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;

    h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 700;
        color: ${({ theme }) => theme.colors.textPrimary};
    }
`;

const CardActions = styled.div`
    display: flex;
    gap: 0.375rem;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.75rem;
`;

const Input = styled.input`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const Select = styled(SimpleSelect)`
    padding: 0.75rem 0.875rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    width: 100%;
    font-size: 0.95rem;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    transition: border 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-bg);
        outline: none;
    }
`;

const Actions = styled.div`
    display: flex;
    gap: 0.625rem;
`;

const FieldLabel = styled.div`
    font-weight: 700;
    margin-top: 0.25rem;
`;

const RoleGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const RoleChip = styled.div`
    border: 1px solid ${({ theme, $checked }) => ($checked ? 'var(--color-primary)' : theme.colors.border)};
    background: ${({ theme, $checked }) => ($checked ? 'var(--color-primary-bg-soft)' : theme.colors.surfaceAlt)};
    border-radius: 10px;
    padding: 0.375rem 0.625rem;
    label {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.textPrimary};
    }
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-weight: 700;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`;

const SecondaryButton = styled.button`
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.textPrimary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s ease;

    &:hover {
        background: ${({ theme }) => theme.colors.surfaceMuted};
    }
`;

const InlineActions = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const IconButton = styled.button`
    background: ${({ theme }) => theme.colors.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-radius: 8px;
    padding: 0.375rem 0.5rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease, border 0.2s ease;

    &:hover {
        background: var(--color-primary-bg-soft);
        border-color: var(--color-primary);
        color: var(--color-primary);
    }
`;

const RoleList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
`;

const RoleBadge = styled.span`
    background: var(--color-primary-bg-soft);
    color: var(--color-primary);
    padding: 0.25rem 0.5rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    z-index: 2000;
`;

const ModalCard = styled.div`
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 12px;
    padding: 1rem;
    width: min(900px, 100%);
    box-shadow: 0 10px 30px rgba(0,0,0,0.12);
`;

const Accordion = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    overflow: hidden;
`;

const AccordionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0.875rem;
    cursor: pointer;
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 700;
`;

const AccordionBody = styled.div`
    padding: 0.75rem 0.875rem;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.surface};
`;

const FiltersBar = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.625rem;
    margin: 0.75rem 0;
`;

const SearchInput = styled(Input)`
    grid-column: span 2;

    @media (max-width: 900px) {
        grid-column: span 1;
    }
`;

const getAdminTableStyles = (theme) => ({
    headRow: {
        style: {
            backgroundColor: theme.colors.tableHeaderBg || '#f9fafb',
            border: `1px solid ${theme.colors.border}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            minHeight: '48px',
        },
    },
    headCells: {
        style: {
            color: theme.colors.textMuted,
            fontWeight: 700,
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
        },
    },
    rows: {
        style: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.textPrimary,
            borderLeft: `1px solid ${theme.colors.border}`,
            borderRight: `1px solid ${theme.colors.border}`,
            borderBottom: `1px solid ${theme.colors.border}`,
            minHeight: '52px',
        },
        highlightOnHoverStyle: {
            backgroundColor: theme.colors.tableRowHoverBg || '#f8fafc',
            color: theme.colors.textPrimary,
        },
    },
    pagination: {
        style: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.border}`,
            borderTop: 'none',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
        },
    },
});

export default AdminUsers;
